import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { useToast } from '../context/ToastContext';
import { apiClient } from '../api/client';

const schema = z.object({
  new_password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string()
}).refine((data) => data.new_password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const ResetPassword = () => {
  const [token, setToken] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  
  const { register, handleSubmit, formState: { errors, isSubmitting }, watch } = useForm({
    resolver: zodResolver(schema)
  });

  const password = watch('new_password', '');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get('token');
    if (!tokenParam) {
      addToast('Invalid or missing reset token', 'error');
      navigate('/');
    } else {
      setToken(tokenParam);
    }
  }, [location, navigate, addToast]);
  
  const getPasswordStrength = (pass) => {
    if (pass.length === 0) return { label: '', class: '' };
    let strength = 0;
    if (pass.length > 7) strength += 1;
    if (pass.match(/[a-z]+/)) strength += 1;
    if (pass.match(/[A-Z]+/)) strength += 1;
    if (pass.match(/[0-9]+/)) strength += 1;
    if (pass.match(/[$@#&!]+/)) strength += 1;
    
    if (strength <= 2) return { label: 'Weak', class: 'strength-weak' };
    if (strength === 3) return { label: 'Fair', class: 'strength-fair' };
    if (strength === 4) return { label: 'Good', class: 'strength-good' };
    return { label: 'Strong', class: 'strength-strong' };
  };

  const strength = getPasswordStrength(password);

  const onSubmit = async (data) => {
    try {
      await apiClient.post('/password-reset/reset', {
        token,
        new_password: data.new_password
      });
      addToast('Password reset successfully. You can now login.', 'success');
      navigate('/login');
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to reset password. Link may be expired.', 'error');
    }
  };

  if (!token) return null;

  return (
    <div className="auth-page">
      <div className="auth-card">
        <Link to="/" className="auth-logo justify-center">
          <div className="auth-logo-icon">M</div>
          <div>Million<span>Minds</span></div>
        </Link>
        
        <div className="text-center mb-8">
          <h1 className="auth-title">Create New Password</h1>
          <p className="auth-subtitle">Please enter your new password below.</p>
        </div>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="w-full">
            <Input
              label="New Password"
              type="password"
              placeholder="••••••••"
              {...register('new_password')}
              error={errors.new_password?.message}
            />
            {password && (
              <div className={`password-strength ${strength.class} mb-4`}>
                <div className="flex justify-between items-center mb-1">
                  <span className="strength-label">Password strength</span>
                  <span className="strength-label font-semi">{strength.label}</span>
                </div>
                <div className="strength-bar"><div className="strength-fill"></div></div>
              </div>
            )}
          </div>
          
          <Input
            label="Confirm New Password"
            type="password"
            placeholder="••••••••"
            {...register('confirmPassword')}
            error={errors.confirmPassword?.message}
          />
          
          <div className="mt-8">
            <Button type="submit" variant="primary" className="w-full" size="lg" isLoading={isSubmitting}>
              Reset Password
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;